import React, { useState, useEffect } from "react";
import {
  Plus, Search, Users, CreditCard, Eye, Edit, Trash2,
  FileText, ArrowLeft, User, Check, X, ChevronDown, ChevronUp,
  Upload, Download, CheckCircle, XCircle, Filter, DollarSign,
  RefreshCw, Bell, Layers, TrendingUp, MapPin, Calendar,
  BarChart2, Award, Shield, ChevronRight, AlertTriangle, Settings,
} from "lucide-react";
import { ChelekoService } from "../components/cheleko/ChelekoService";
import ChelekoConfiguration from "./ChelekoConfiguration";

// ─── Design tokens (matches Majanga / Jamii flat system) ─────────────────────
const NAV  = "#001f3f";
const TEAL = "#0d9488";

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    approved:     "bg-emerald-100 text-emerald-700 border border-emerald-200",
    rejected:     "bg-red-100 text-red-700 border border-red-200",
    pending:      "bg-amber-100 text-amber-700 border border-amber-200",
    submitted:    "bg-blue-100 text-blue-700 border border-blue-200",
    draft:        "bg-slate-100 text-slate-600 border border-slate-200",
    active:       "bg-teal-100 text-teal-700 border border-teal-200",
    inactive:     "bg-slate-100 text-slate-600 border border-slate-200",
    accepted:     "bg-emerald-100 text-emerald-700 border border-emerald-200",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide capitalize ${map[status?.toLowerCase()] || map.draft}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
};

// Every section the CFN/joining form actually collects - used everywhere a
// member's full details are shown, so nothing filled in during registration
// stays invisible (kin/emergency contacts and medical info were previously
// left out entirely).
const memberDetailSections = (m) => [
  { title: "Personal", data: [
    ["Full Name", m.full_name], ["Phone", m.phone_number], ["Email", m.email],
    ["Gender", m.gender], ["Marital Status", m.marital_status], ["Spouse", m.spouse_name],
    ["Date of Birth", m.date_of_birth ? new Date(m.date_of_birth).toLocaleDateString() : null],
  ]},
  { title: "Address", data: [
    ["Current", m.current_address], ["Permanent", m.permanent_address], ["Business", m.business_address],
  ]},
  { title: "Professional", data: [
    ["Occupation", m.occupation], ["Employer", m.employer], ["Work Address", m.work_address],
    ["Monthly Income", m.monthly_income ? `TZS ${parseFloat(m.monthly_income).toLocaleString()}` : null],
  ]},
  { title: "Family", data: [
    ["Father", m.father_name], ["Mother", m.mother_name],
    ["Father-in-Law", m.father_in_law_name || m.father_in_law],
    ["Mother-in-Law", m.mother_in_law_name],
    ["Children", m.number_of_children], ["Children Names", m.children_names],
  ]},
  { title: "Next of Kin / Emergency", data: [
    ["Next of Kin", m.next_of_kin], ["Kin Phone", m.next_of_kin_phone], ["Relationship", m.next_of_kin_relationship],
    ["Emergency Contact", m.emergency_contact_name], ["Emergency Phone", m.emergency_contact_phone],
  ]},
  { title: "Medical", data: [
    ["Medical Checkup", m.medical_check_up || m.medical_checkup],
    ["Financed by ENF", m.medical_check_up_financed_by_enf ? "Yes" : "No"],
    ["Health Insurance", m.has_health_insurance ? "Yes" : "No"],
    ["Insurance Provider", m.health_insurance_provider],
  ]},
  { title: "Contribution", data: [
    ["Amount 1", m.contribution_amount1 ? `TZS ${parseFloat(m.contribution_amount1).toLocaleString()}` : null],
    ["Amount 2", m.contribution_amount2 ? `TZS ${parseFloat(m.contribution_amount2).toLocaleString()}` : null],
    ["Total", m.contribution_total ? `TZS ${parseFloat(m.contribution_total).toLocaleString()}` : null],
    ["Expected Monthly", m.expected_monthly_contribution ? `TZS ${parseFloat(m.expected_monthly_contribution).toLocaleString()}` : null],
  ]},
  { title: "Banking", data: [
    ["Bank", m.bank_name], ["Account No.", m.account_number], ["Account Name", m.account_name],
  ]},
  { title: "Witness", data: [
    ["Name", m.witness_name], ["Title", m.witness_designation], ["Phone", m.witness_phone],
  ]},
];

const MemberFullDetails = ({ m }) => (
  <div>
    {m.profile_image_url && (
      <div className="flex items-center gap-3 mb-3">
        <img src={m.profile_image_url} alt={m.full_name || "Member"} className="w-16 h-16 rounded-xl object-cover border border-slate-200" />
        <span className="text-xs text-slate-400">Photo attached during registration</span>
      </div>
    )}
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {memberDetailSections(m).map(s => (
        <div key={s.title} className="bg-white rounded-xl p-3.5 border border-slate-100">
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 pb-1 border-b border-slate-100">{s.title}</p>
          {s.data.map(([l, v]) => (
            <div key={l} className="flex justify-between gap-2 py-0.5">
              <span className="text-xs text-slate-400">{l}</span>
              <span className="text-xs font-medium text-slate-700 text-right truncate max-w-[140px]">{v || "N/A"}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, accent = NAV }) => (
  <div className="bg-white rounded-xl p-5 border border-slate-200 flex items-center gap-4">
    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: accent + "18" }}>
      <Icon size={20} style={{ color: accent }} />
    </div>
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-xl font-bold text-slate-800 leading-tight">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Tab button ───────────────────────────────────────────────────────────────
const Tab = ({ icon: Icon, label, active, onClick, badge }) => (
  <button onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
      active ? "border-teal-500 text-teal-600" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
    }`}>
    <Icon size={15} />
    <span>{label}</span>
    {badge > 0 && (
      <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{badge}</span>
    )}
  </button>
);

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ title, subtitle, action }) => (
  <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
    <div>
      <h2 className="text-sm font-bold text-slate-800">{title}</h2>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);

// ─── Input ────────────────────────────────────────────────────────────────────
const Input = ({ label, ...props }) => (
  <div>
    {label && <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}</label>}
    <input className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-transparent" {...props} />
  </div>
);

// ─── Primary button ───────────────────────────────────────────────────────────
const Btn = ({ children, onClick, disabled, variant = "primary", size = "md", className = "" }) => {
  const base = "inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-50";
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-sm" };
  const variants = {
    primary: "text-white hover:opacity-90",
    outline: "border border-slate-200 text-slate-700 hover:bg-slate-50",
    danger:  "bg-red-500 text-white hover:bg-red-600",
    teal:    "text-white hover:opacity-90",
  };
  const bg = variant === "primary" ? { background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }
           : variant === "teal"    ? { backgroundColor: TEAL }
           : {};
  return (
    <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
      style={bg} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
const Cheleko = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [gangs, setGangs]         = useState([]);
  const [cfnform, setCfnform]     = useState({ results: [] });
  const [gangRequests, setGangRequests] = useState([]);
  const [requestStatusFilter, setRequestStatusFilter] = useState("all");
  const [gangDetails, setGangDetails]   = useState(null);
  const [gangLeadership, setGangLeadership] = useState({}); // { gangId: [...holderRows] }
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCFNForm, setSelectedCFNForm] = useState(null);
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const [deletingFormId, setDeletingFormId]   = useState(null);

  const [newGang, setNewGang] = useState({
    name:"", description:"", location:"", is_private:false,
    contact_email:"", contact_phone:"", joining_fee:"", specification:"Cheleko",
  });
  const [editGang, setEditGang] = useState({ id: null });

  const [showContributionModal, setShowContributionModal] = useState(false);
  const [contributionHistory, setContributionHistory]     = useState(null);
  const [showHistoryModal, setShowHistoryModal]           = useState(false);
  const [newContribution, setNewContribution] = useState({
    amount:"", description:"", memberId:null, gangId:null,
    contribution_date:null, is_joining_fee:false,
  });

  const [sponsorForms, setSponsorForms]   = useState([]);
  const [sponsorRequests, setSponsorRequests] = useState([]);
  const [selectedSponsorRequest, setSelectedSponsorRequest] = useState(null);
  const [sponsorSearchTerm, setSponsorSearchTerm] = useState("");
  const [sponsorStatusFilter, setSponsorStatusFilter] = useState("all");
  const [newSponsorForm, setNewSponsorForm] = useState({
    title:"", description:"", version:"", is_active:true, form_pdf:null,
  });

  // ── Data loaders ────────────────────────────────────────────────────────────
  useEffect(() => { fetchGangs(); }, []);
  useEffect(() => { if (activeTab === "requests")       fetchGangRequests();  }, [activeTab]);
  useEffect(() => { if (activeTab === "sponsorForms")   fetchSponsorForms();  }, [activeTab]);
  useEffect(() => {
    if (activeTab === "sponsorRequests")
      fetchSponsorRequests(sponsorStatusFilter === "all" ? null : sponsorStatusFilter);
  }, [activeTab, sponsorStatusFilter]);

  const fetchGangs = async () => {
    setLoading(true);
    try {
      const [data, cfndata] = await Promise.all([
        ChelekoService.getAllGangs(),
        ChelekoService.getIndividualForms(),
      ]);
      setGangs(Array.isArray(data) ? data : data.results || []);
      setCfnform(cfndata || { results: [] });
      setError(null);
    } catch { setError("Failed to load communities."); }
    finally { setLoading(false); }
  };

  const fetchGangDetails = async (gangId) => {
    setLoading(true);
    try {
      const [data, chainData] = await Promise.all([
        ChelekoService.getGangDetails(gangId),
        fetch(`https://api.jamboricg.com/api/majanga/gang-roles/approval-chain/?gang_id=${gangId}`)
          .then(r => r.json()).catch(() => ({})),
      ]);
      setGangDetails(data);
      // Build leadership list from approval_chain holders + non_chain_roles holders
      const allRoles = [
        ...(chainData.approval_chain || []),
        ...(chainData.non_chain_roles || []),
      ];
      const leaders = [];
      allRoles.forEach(role => {
        if (role.role_type === 'member') return;
        (role.holders || []).forEach(h => {
          leaders.push({ ...h, role_type: role.role_type, role_name: role.name, approval_order: role.approval_order });
        });
      });
      setGangLeadership(prev => ({ ...prev, [gangId]: leaders }));
      setError(null);
    } catch { setError("Failed to load community details."); }
    finally { setLoading(false); }
  };

  const fetchGangRequests = async () => {
    setLoading(true);
    try {
      const data = await ChelekoService.getGangRequest();
      const arr  = data.results || data || [];
      setGangRequests(arr.map(item => ({
        id:         item.id,
        user_name:  item.user?.name  || item.full_name || "Unknown",
        user_email: item.user?.email || "No Email",
        gang_id:    item.gang?.id    || item.gang,
        gang_name:  item.gang_details?.name || item.gang?.name || "Unknown",
        status:     item.status || "pending",
        created_at: item.join_date || item.joining_date || new Date().toISOString(),
        ...item,
      })));
      setError(null);
    } catch { setError("Failed to load requests."); }
    finally { setLoading(false); }
  };

  const fetchContributionHistory = async (membershipId) => {
    setLoading(true);
    try {
      const data = await ChelekoService.getMemberContributionHistory(membershipId);
      setContributionHistory(data);
      setShowHistoryModal(true);
      setError(null);
    } catch { setError("Failed to load contribution history."); }
    finally { setLoading(false); }
  };

  const fetchSponsorForms = async () => {
    setLoading(true);
    try {
      const data = await ChelekoService.getActiveForms();
      setSponsorForms(data.forms || []);
      setError(null);
    } catch { setError("Failed to load sponsor forms."); }
    finally { setLoading(false); }
  };

  const fetchSponsorRequests = async (status = null) => {
    setLoading(true);
    try {
      const data = await ChelekoService.getAllSponsorRequests(status);
      setSponsorRequests(data.requests || []);
      setError(null);
    } catch { setError("Failed to load sponsor requests."); }
    finally { setLoading(false); }
  };

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleAddGang = async () => {
    setLoading(true);
    try {
      const created = await ChelekoService.createGang(newGang);
      setGangs([...gangs, { ...created, members:0, contributions:0 }]);
      setNewGang({ name:"", description:"", location:"", is_private:false, contact_email:"", contact_phone:"", joining_fee:"", specification:"Cheleko" });
      setActiveTab("gangs");
      setError(null);
    } catch { setError("Failed to create community."); }
    finally { setLoading(false); }
  };

  const handleDeleteGang = async (id) => {
    if (!window.confirm("Delete this community?")) return;
    setLoading(true);
    try {
      await ChelekoService.deleteGang(id);
      setGangs(gangs.filter(g => g.id !== id));
      setError(null);
    } catch { setError("Failed to delete community."); }
    finally { setLoading(false); }
  };

  const handleUpdateGang = async () => {
    setLoading(true);
    try {
      const updated = await ChelekoService.updateGang(editGang.id, editGang);
      setGangs(gangs.map(g => g.id === editGang.id ? { ...g, ...updated } : g));
      setEditGang({ id:null });
      setError(null);
    } catch { setError("Failed to update community."); }
    finally { setLoading(false); }
  };

  const handleApproveRequest = async (requestId, type) => {
    setLoading(true);
    try {
      await ChelekoService.approveGangRequest(requestId, { status: type });
      setGangRequests(gangRequests.map(r => r.id === requestId ? { ...r, status: type } : r));
      if (selectedRequest?.id === requestId) setSelectedRequest({ ...selectedRequest, status: type });
      setError(null);
    } catch { setError("Failed to process request."); }
    finally { setLoading(false); }
  };

  // Rejected requests otherwise sit around forever with no way to clear them
  // out - this lets an admin remove one permanently once it's no longer wanted.
  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Permanently delete this rejected request? This cannot be undone.")) return;
    setLoading(true);
    try {
      await ChelekoService.deleteGangRequest(requestId);
      setGangRequests(gangRequests.filter(r => r.id !== requestId));
      if (selectedRequest?.id === requestId) setSelectedRequest(null);
      setError(null);
    } catch { setError("Failed to delete request."); }
    finally { setLoading(false); }
  };

  const handleAddContribution = async () => {
    if (!newContribution.amount || isNaN(newContribution.amount)) { setError("Valid amount required"); return; }
    setLoading(true);
    try {
      await ChelekoService.addContribution({
        membership:        newContribution.memberId,
        amount:            parseFloat(newContribution.amount),
        description:       newContribution.description,
        contribution_date: new Date().toISOString().split("T")[0],
        is_joining_fee:    false,
      });
      await fetchGangDetails(newContribution.gangId);
      setNewContribution({ amount:"", description:"", memberId:null, gangId:null, contribution_date:null, is_joining_fee:false });
      setShowContributionModal(false);
      setError(null);
    } catch { setError("Failed to add contribution."); }
    finally { setLoading(false); }
  };

  // A rejected membership is not a member — keep them out of the gang's member lists.
  const activeMembers = (members) => (members || []).filter(m => m.status !== "rejected");

  const handleDeleteCFNForm = async (formId, formName) => {
    if (!window.confirm(`Delete CFN form for "${formName}"?`)) return;
    setDeletingFormId(formId);
    setLoading(true);
    try {
      await ChelekoService.deleteCFNForm(formId);
      setCfnform(prev => ({ ...prev, results: prev.results.filter(f => f.id !== formId) }));
      setError(null);
    } catch { setError("Failed to delete CFN form."); }
    finally { setDeletingFormId(null); setLoading(false); }
  };

  const handleUploadSponsorForm = async () => {
    if (!newSponsorForm.title || !newSponsorForm.form_pdf) { setError("Title and PDF required"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(newSponsorForm).forEach(([k,v]) => fd.append(k, v));
      await ChelekoService.uploadSponsorForm(fd);
      setNewSponsorForm({ title:"", description:"", version:"", is_active:true, form_pdf:null });
      document.getElementById("cheleko-sponsor-upload").value = "";
      await fetchSponsorForms();
      setError(null);
    } catch { setError("Failed to upload sponsor form."); }
    finally { setLoading(false); }
  };

  const handleAdminReview = async (requestId, status, notes) => {
    setLoading(true);
    try {
      await ChelekoService.adminReviewRequest(requestId, { status, admin_notes: notes });
      await fetchSponsorRequests(sponsorStatusFilter === "all" ? null : sponsorStatusFilter);
      setSelectedSponsorRequest(null);
      setError(null);
    } catch { setError("Failed to review request."); }
    finally { setLoading(false); }
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const filteredGangs = gangs.filter(g =>
    g.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const pendingCount  = gangRequests.filter(r => r.status === "pending").length;

  // Join requests: filter by status, then group by gang so each community's
  // requests are reviewed together instead of one long mixed list.
  const REQUEST_FILTERS = [
    { id: "all",      label: "All"      },
    { id: "pending",  label: "Pending"  },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
  ];
  const requestCountByStatus = (statusId) =>
    statusId === "all" ? gangRequests.length : gangRequests.filter(r => r.status === statusId).length;

  const visibleRequests = gangRequests.filter(r => {
    const matchesStatus = requestStatusFilter === "all" || r.status === requestStatusFilter;
    const term = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm
      || r.user_name?.toLowerCase().includes(term)
      || r.user_email?.toLowerCase().includes(term)
      || r.gang_name?.toLowerCase().includes(term);
    return matchesStatus && matchesSearch;
  });

  const requestGroups = Object.values(
    visibleRequests.reduce((groups, r) => {
      const key = r.gang ?? r.gang_id ?? r.gang_name ?? "unknown";
      if (!groups[key]) groups[key] = { key, name: r.gang_name || "Unknown community", requests: [] };
      groups[key].requests.push(r);
      return groups;
    }, {})
  ).sort((a, b) => a.name.localeCompare(b.name));
  const totalContribs = gangs.reduce((s, g) => s + (g.total_contribution || 0), 0);
  const totalMembers  = gangs.reduce((s, g) => s + (g.member_count || 0), 0);
  const filteredSponsorRequests = sponsorRequests.filter(r => {
    const q = sponsorSearchTerm.toLowerCase();
    return (r.request_title?.toLowerCase().includes(q) ||
            r.reference_number?.toLowerCase().includes(q) ||
            r.user_info?.name?.toLowerCase().includes(q)) &&
           (sponsorStatusFilter === "all" || r.status === sponsorStatusFilter);
  });

  const tabs = [
    { id:"dashboard",       label:"Dashboard",       icon:BarChart2 },
    { id:"gangs",           label:"Communities",      icon:Layers },
    { id:"contributions",   label:"Contributions",    icon:TrendingUp },
    { id:"members",         label:"Memberships",      icon:Users },
    { id:"individual",      label:"Individual CFN",   icon:FileText },
    { id:"requests",        label:"Join Requests",    icon:Bell, badge:pendingCount },
    { id:"sponsorForms",    label:"Sponsor Forms",    icon:Upload },
    { id:"sponsorRequests", label:"Sponsor Requests", icon:Award },
    { id:"configuration",   label:"Configuration",    icon:Settings },
  ];

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex flex-col h-full min-h-screen bg-slate-50">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: TEAL }}>
            <Shield size={16} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800 leading-none">Cheleko Hub</p>
            <p className="text-[10px] text-slate-400">Community Management System</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Layers size={12} style={{ color:TEAL }}/>{gangs.length} communities</span>
            <span className="flex items-center gap-1"><Users size={12} className="text-blue-500"/>{totalMembers} members</span>
            {pendingCount > 0 && <span className="flex items-center gap-1 text-amber-600 font-semibold"><Bell size={12}/>{pendingCount} pending</span>}
          </div>
          <button onClick={fetchGangs} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <RefreshCw size={15}/>
          </button>
          <button
            onClick={() => window.location.href = "/general-contributions"}
            title="Open General Contributions"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-xl shadow-sm"
            style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
            <DollarSign size={13}/> General Contributions
          </button>
          {activeTab === "gangs" && (
            <Btn size="sm" onClick={() => setActiveTab("addGang")}>
              <Plus size={13}/> New Community
            </Btn>
          )}
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100 px-4 flex-shrink-0 overflow-x-auto">
        <div className="flex">
          {tabs.map(t => (
            <Tab key={t.id} icon={t.icon} label={t.label}
              active={activeTab === t.id} onClick={() => setActiveTab(t.id)} badge={t.badge} />
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mt-3 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex-shrink-0">
          <AlertTriangle size={15} className="flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}><X size={13}/></button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3" style={{ color:TEAL }}>
            <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor:`${TEAL} transparent transparent transparent` }}/>
            <span className="text-sm font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* ── Content ────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto p-6">

        {/* ══ DASHBOARD ════════════════════════════════════════════════ */}
        {!loading && activeTab === "dashboard" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Layers}     label="Communities"         value={gangs.length}        accent={NAV} />
              <StatCard icon={Users}      label="Total Members"       value={totalMembers}          accent={TEAL} />
              <StatCard icon={Bell}       label="Pending Requests"    value={pendingCount}          accent="#f59e0b" />
              <StatCard icon={TrendingUp} label="Total Contributions" value={`TZS ${totalContribs.toLocaleString()}`} sub="all time" accent="#3b82f6" />
            </div>

            <Card>
              <CardHeader title="Communities Overview"
                subtitle={gangs.length > 6 ? `Showing 6 of ${gangs.length} — this is a preview, not the full count` : undefined}
                action={<button onClick={() => setActiveTab("gangs")} className="text-xs font-medium flex items-center gap-1" style={{ color:TEAL }}>View all <ChevronRight size={14}/></button>} />
              <div className="divide-y divide-slate-50">
                {gangs.slice(0, 6).map(g => (
                  <div key={g.id} className="px-6 py-3.5 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
                      style={{ backgroundColor: TEAL }}>
                      {g.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{g.name}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={10}/>{g.location}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-700">{g.member_count || 0}</p>
                      <p className="text-xs text-slate-400">members</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${g.is_private ? "bg-red-50 text-red-600":"bg-green-50 text-green-600"}`}>
                      {g.is_private ? "Private":"Public"}
                    </span>
                  </div>
                ))}
                {gangs.length === 0 && <p className="text-center py-8 text-slate-400 text-sm">No communities yet</p>}
              </div>
            </Card>

            {pendingCount > 0 && (
              <Card>
                <CardHeader title="Pending Join Requests"
                  action={<button onClick={() => setActiveTab("requests")} className="text-xs font-medium flex items-center gap-1" style={{ color:TEAL }}>View all <ChevronRight size={14}/></button>} />
                <div className="divide-y divide-slate-50">
                  {gangRequests.filter(r => r.status === "pending").slice(0, 5).map(r => (
                    <div key={r.id} className="px-6 py-3.5 flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <User size={14} className="text-slate-500"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{r.user_name}</p>
                        <p className="text-xs text-slate-400">{r.gang_name}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleApproveRequest(r.id,"approved")} className="p-1.5 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg"><Check size={13}/></button>
                        <button onClick={() => handleApproveRequest(r.id,"rejected")} className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg"><X size={13}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ══ COMMUNITIES ════════════════════════════════════════════════ */}
        {!loading && activeTab === "gangs" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="text" placeholder="Search communities..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"/>
              </div>
              <span className="text-sm text-slate-400">{filteredGangs.length} communities</span>
            </div>
            <Card>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {["Community","Location","Type","Members","Contributions","Actions"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredGangs.map(gang => (
                    <tr key={gang.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ backgroundColor: NAV }}>
                            {gang.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{gang.name}</p>
                            <p className="text-xs text-slate-400 truncate max-w-[140px]">{gang.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        <div className="flex items-center gap-1"><MapPin size={12} className="text-slate-400"/>{gang.location?.split(" ").slice(0,3).join(" ")}</div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${gang.is_private?"bg-red-50 text-red-600":"bg-green-50 text-green-600"}`}>
                          {gang.is_private?"Private":"Public"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-700">{gang.member_count||0}</td>
                      <td className="px-5 py-4 text-sm font-semibold" style={{ color:TEAL }}>
                        TZS {(gang.total_contribution||0).toLocaleString()}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex gap-1">
                          <button onClick={() => setEditGang({...gang, specification:gang.specification||"Cheleko"})} className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg"><Edit size={14}/></button>
                          <button onClick={() => handleDeleteGang(gang.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredGangs.length === 0 && <div className="py-14 text-center text-slate-400"><Layers size={28} className="mx-auto mb-2 opacity-30"/><p className="text-sm">No communities found</p></div>}
            </Card>
          </div>
        )}

        {/* ══ ADD COMMUNITY ══════════════════════════════════════════════ */}
        {!loading && activeTab === "addGang" && (
          <div className="max-w-2xl">
            <button onClick={() => setActiveTab("gangs")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-5"><ArrowLeft size={15}/> Back</button>
            <Card>
              <CardHeader title="Create New Community" />
              <div className="p-6 grid grid-cols-2 gap-4">
                {[
                  { label:"Community Name", name:"name", placeholder:"Enter name" },
                  { label:"Location",       name:"location", placeholder:"City, Country" },
                  { label:"Contact Email",  name:"contact_email", placeholder:"email@example.com", type:"email" },
                  { label:"Contact Phone",  name:"contact_phone", placeholder:"+255712345678" },
                  { label:"Joining Fee (TZS)", name:"joining_fee", placeholder:"5000.00" },
                ].map(f => (
                  <Input key={f.name} label={f.label} type={f.type||"text"} name={f.name} value={newGang[f.name]||""}
                    onChange={e => setNewGang({...newGang, [e.target.name]: e.target.value})} placeholder={f.placeholder} />
                ))}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Specification</label>
                  <select value={newGang.specification} onChange={e => setNewGang({...newGang, specification: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300">
                    <option value="All">All</option><option value="Majanga">Majanga</option><option value="Cheleko">Cheleko</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                  <textarea name="description" value={newGang.description} onChange={e => setNewGang({...newGang, description:e.target.value})}
                    rows={3} placeholder="Community purpose..."
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"/>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="is_private_new" checked={newGang.is_private} onChange={e => setNewGang({...newGang, is_private:e.target.checked})} className="w-4 h-4 rounded"/>
                  <label htmlFor="is_private_new" className="text-sm text-slate-600">Private Community</label>
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 pb-6 pt-2 border-t border-slate-100">
                <Btn variant="outline" onClick={() => setActiveTab("gangs")}>Cancel</Btn>
                <Btn onClick={handleAddGang} disabled={loading}>Create Community</Btn>
              </div>
            </Card>
          </div>
        )}

        {/* ══ CONTRIBUTIONS ══════════════════════════════════════════════ */}
        {!loading && activeTab === "contributions" && (
          <div className="space-y-3">
            {gangs.map(gang => (
              <Card key={gang.id}>
                <button className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                  onClick={() => gangDetails?.id === gang.id ? setGangDetails(null) : fetchGangDetails(gang.id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor:NAV }}>
                      {gang.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-slate-800">{gang.name}</p>
                      <p className="text-xs text-slate-400">{gang.member_count||0} members</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-bold" style={{ color:TEAL }}>TZS {(gang.total_contribution||0).toLocaleString()}</p>
                      <p className="text-xs text-slate-400">Total</p>
                    </div>
                    {gangDetails?.id === gang.id ? <ChevronUp size={16} className="text-slate-400"/> : <ChevronDown size={16} className="text-slate-400"/>}
                  </div>
                </button>
                {gangDetails?.id === gang.id && (
                  <div className="border-t border-slate-100 p-5">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50">
                          {["Member","Email","Total","Avg / Count","Actions"].map(h => (
                            <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {activeMembers(gangDetails.members).map(m => {
                          const mid = m.membership_id || m.id;
                          const expanded = expandedMemberId === mid;
                          return (
                          <React.Fragment key={m.id}>
                          <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedMemberId(expanded ? null : mid)}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor:NAV }}>
                                  {(m.user?.name||"?")[0].toUpperCase()}
                                </div>
                                <p className="text-sm font-semibold text-slate-800">{m.user?.name||"Unknown"}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">{m.user?.email||"N/A"}</td>
                            <td className="px-4 py-3 text-sm font-semibold" style={{ color:TEAL }}>TZS {m.contribution_statistics?.total?.toLocaleString()||0}</td>
                            <td className="px-4 py-3 text-sm text-slate-500">TZS {m.contribution_statistics?.average?.toLocaleString()||0} ({m.contribution_statistics?.count||0})</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                <Btn size="sm" variant="primary" onClick={() => {
                                  setNewContribution({amount:"",description:"",memberId:m.membership_id,gangId:gang.id,contribution_date:new Date().toISOString().split("T")[0],is_joining_fee:false});
                                  setShowContributionModal(true);
                                }}>Add</Btn>
                                <Btn size="sm" variant="teal" onClick={() => fetchContributionHistory(m.membership_id)}>History</Btn>
                                <button onClick={() => setExpandedMemberId(expanded ? null : mid)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg" title="View joining form details">
                                  {expanded ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expanded && (
                            <tr><td colSpan="5" className="px-4 py-4 bg-slate-50">
                              <MemberFullDetails m={m} />
                            </td></tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* ══ MEMBERS ════════════════════════════════════════════════════ */}
        {!loading && activeTab === "members" && (
          <div className="space-y-3">
            <div className="relative max-w-xs">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"/>
            </div>
            {filteredGangs.map(gang => (
              <Card key={gang.id}>
                <button className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                  onClick={() => gangDetails?.id === gang.id ? setGangDetails(null) : fetchGangDetails(gang.id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor:TEAL }}>
                      {gang.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-slate-800">{gang.name}</p>
                      <p className="text-xs text-slate-400">{gang.member_count||0} members · {gang.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold" style={{ color:TEAL }}>TZS {(gang.total_contribution||0).toLocaleString()}</span>
                    {gangDetails?.id === gang.id ? <ChevronUp size={16} className="text-slate-400"/> : <ChevronDown size={16} className="text-slate-400"/>}
                  </div>
                </button>
                {gangDetails?.id === gang.id && (
                  <div className="border-t border-slate-100 p-5 space-y-4">
                    {/* Leadership board */}
                    {(() => {
                      const leaders = gangLeadership[gang.id] || [];
                      const ROLE_CFG = {
                        chairperson: { icon:'👑', bg:'bg-amber-50', border:'border-amber-200', text:'text-amber-700', label:'Mwenyekiti' },
                        treasurer:   { icon:'💰', bg:'bg-green-50',  border:'border-green-200',  text:'text-green-700',  label:'Mhazini'     },
                        secretary:   { icon:'📖', bg:'bg-blue-50',   border:'border-blue-200',   text:'text-blue-700',   label:'Katibu'      },
                        vice_chair:  { icon:'⭐', bg:'bg-purple-50', border:'border-purple-200', text:'text-purple-700', label:'Makamu'      },
                        custom:      { icon:'⚡', bg:'bg-teal-50',   border:'border-teal-200',   text:'text-teal-700',   label:'Maalum'      },
                      };
                      if (!leaders.length) return null;
                      return (
                        <div>
                          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <span>👑</span> Gang Leadership
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                            {leaders
                              .sort((a,b) => (a.approval_order||99)-(b.approval_order||99))
                              .map((leader, i) => {
                                const cfg = ROLE_CFG[leader.role_type] || ROLE_CFG.custom;
                                return (
                                  <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${cfg.bg} ${cfg.border}`}>
                                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 ${cfg.bg}`}>
                                      {cfg.icon}
                                    </div>
                                    <div className="min-w-0">
                                      <p className={`text-[10px] font-bold uppercase tracking-wide ${cfg.text}`}>{leader.role_name || cfg.label}</p>
                                      <p className="text-xs font-semibold text-slate-800 truncate">{leader.name || "—"}</p>
                                    </div>
                                  </div>
                                );
                              })
                            }
                          </div>
                        </div>
                      );
                    })()}

                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Users size={12}/>
                      All Members ({activeMembers(gangDetails.members).length})
                    </p>
                    <div className="flex flex-col gap-2">
                      {activeMembers(gangDetails.members).map(m => {
                        const mid = m.membership_id || m.id;
                        const expanded = expandedMemberId === mid;
                        return (
                        <div key={m.id} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
                          <button type="button" onClick={() => setExpandedMemberId(expanded ? null : mid)}
                            className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-slate-100 transition-colors">
                            {m.profile_image_url ? (
                              <img src={m.profile_image_url} alt={m.full_name||"Member"} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor:NAV }}>
                                {(m.user?.name||"?")[0].toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-800 truncate">{m.user?.name||"Unknown"}</p>
                              <p className="text-xs text-slate-400 truncate">{m.user?.email||"N/A"}</p>
                              <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                <Calendar size={10}/> {m.joining_date ? new Date(m.joining_date).toLocaleDateString():"N/A"}
                              </p>
                            </div>
                            {expanded ? <ChevronUp size={15} className="text-slate-400 flex-shrink-0"/> : <ChevronDown size={15} className="text-slate-400 flex-shrink-0"/>}
                          </button>
                          {expanded && (
                            <div className="px-3.5 pb-3.5">
                              {/* These fields are already returned by the API (filled during
                                  joining) but were never rendered anywhere - the data wasn't
                                  lost, it just wasn't shown. */}
                              <MemberFullDetails m={m} />
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* ══ INDIVIDUAL CFN ═════════════════════════════════════════════ */}
        {!loading && activeTab === "individual" && (
          <div className="space-y-4">
            <div className="relative max-w-xs">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input type="text" placeholder="Search CFN forms..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"/>
            </div>
            <Card>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {["CEFN No.","Name","Email","Location","Date",""].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {(cfnform.results||[])
                    .filter(f => f.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || f.cefn_no?.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map(form => (
                    <React.Fragment key={form.id}>
                      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedCFNForm(selectedCFNForm===form.id?null:form.id)}>
                        <td className="px-5 py-3.5"><span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded-lg">{form.cefn_no}</span></td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            {form.profile_image
                              ? <img src={form.profile_image} className="w-8 h-8 rounded-full object-cover" alt=""/>
                              : <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><User size={14} className="text-slate-400"/></div>
                            }
                            <div>
                              <p className="text-sm font-medium text-slate-800">{form.full_name}</p>
                              <p className="text-xs text-slate-400">{form.user?.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-500">{form.prepared_by_email}</td>
                        <td className="px-5 py-3.5 text-sm text-slate-500">{form.current_domicile_address?.split(",")[0]}</td>
                        <td className="px-5 py-3.5 text-sm text-slate-500">{new Date(form.created_at).toLocaleDateString()}</td>
                        <td className="px-5 py-3.5">{selectedCFNForm===form.id?<ChevronUp size={15}/>:<ChevronDown size={15}/>}</td>
                      </tr>
                      {selectedCFNForm===form.id && (
                        <tr><td colSpan="6" className="px-5 py-4 bg-slate-50">
                          {/* Group & Membership — a CFN form has no gang of its own (by
                              design), but the same person is very often also a group
                              member; this is a plain cross-reference by user, so admins
                              can see that link without it being lost. */}
                          <div className="bg-white rounded-xl p-3.5 border border-slate-100 mb-3">
                            <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 pb-1 border-b border-slate-100">Group &amp; Membership</p>
                            {(form.gang_memberships||[]).length > 0 ? (
                              <div className="space-y-2">
                                {form.gang_memberships.map((gm, i) => (
                                  <div key={i} className="flex items-center justify-between gap-3 text-xs">
                                    <div>
                                      <span className="font-medium text-slate-700">{gm.gang_name}</span>
                                      <span className="text-slate-400 ml-2">({gm.gang_specification})</span>
                                    </div>
                                    <StatusBadge status={gm.status} />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-400">Not a member of any community group</p>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {[
                              { title:"Personal",     data:[["Spouse",form.spouse_name],["Birthday",form.birthday_date?new Date(form.birthday_date).toLocaleDateString():"N/A"],["Joining",form.joining_date?new Date(form.joining_date).toLocaleDateString():"N/A"]] },
                              { title:"Address",      data:[["Business",form.business_address],["Current",form.current_domicile_address],["Permanent",form.perm_domicile_address]] },
                              { title:"Family",       data:[["Father",form.father_name],["Mother",form.mother_name],["Father-in-Law",form.father_in_law]] },
                              { title:"Medical",      data:[["Checkup",form.medical_checkup?"Yes":"No"],["Insurance",form.health_insurance?"Yes":"No"],["ENF",form.medical_checkup_financed_by_enf?"Yes":"No"]] },
                              { title:"Contribution", data:[["Amt 1",`TZS ${parseFloat(form.contribution_amount1||0).toLocaleString()}`],["Amt 2",`TZS ${parseFloat(form.contribution_amount2||0).toLocaleString()}`],["Total",`TZS ${parseFloat(form.contribution_total||0).toLocaleString()}`]] },
                              { title:"Witness",      data:[["Name",form.witness_name],["Title",form.witness_designation],["Phone",form.witness_phone]] },
                            ].map(s => (
                              <div key={s.title} className="bg-white rounded-xl p-3.5 border border-slate-100">
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 pb-1 border-b border-slate-100">{s.title}</p>
                                {s.data.map(([l,v]) => (
                                  <div key={l} className="flex justify-between gap-2 py-0.5">
                                    <span className="text-xs text-slate-400">{l}</span>
                                    <span className="text-xs font-medium text-slate-700 text-right truncate max-w-[110px]">{v||"N/A"}</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-end mt-3">
                            <Btn variant="danger" size="sm" onClick={() => handleDeleteCFNForm(form.id, form.full_name)} disabled={deletingFormId===form.id}>
                              <Trash2 size={13}/>{deletingFormId===form.id?"Deleting...":"Delete Form"}
                            </Btn>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {/* ══ JOIN REQUESTS ══════════════════════════════════════════════ */}
        {!loading && activeTab === "requests" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative max-w-xs">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="text" placeholder="Search requests..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"/>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {REQUEST_FILTERS.map(f => {
                  const active = requestStatusFilter === f.id;
                  return (
                    <button key={f.id} onClick={() => setRequestStatusFilter(f.id)}
                      className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        active ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}>
                      {f.label}
                      <span className={`min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                        active ? "bg-white text-slate-800" : "bg-slate-100 text-slate-500"}`}>
                        {requestCountByStatus(f.id)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {requestGroups.length === 0 ? (
              <Card>
                <div className="py-14 text-center text-slate-400">
                  <Bell size={28} className="mx-auto mb-2 opacity-30"/>
                  <p className="text-sm">
                    {gangRequests.length === 0 ? "No join requests" : "No requests match this filter"}
                  </p>
                </div>
              </Card>
            ) : requestGroups.map(group => {
              const groupPending = group.requests.filter(r => r.status === "pending").length;
              return (
                <Card key={group.key}>
                  <div className="flex items-center justify-between gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
                        <span className="text-white text-xs font-bold">{group.name?.[0]?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{group.name}</p>
                        <p className="text-xs text-slate-400">
                          {group.requests.length} request{group.requests.length === 1 ? "" : "s"}
                          {groupPending > 0 && ` · ${groupPending} pending`}
                        </p>
                      </div>
                    </div>
                    {groupPending > 0 && (
                      <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                        {groupPending} to review
                      </span>
                    )}
                  </div>
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-white border-b border-slate-100">
                        {["Member","Date","Status","Actions"].map(h => (
                          <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {group.requests.map(req => (
                        <tr key={req.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><User size={13} className="text-slate-500"/></div>
                              <div>
                                <p className="text-sm font-medium text-slate-800">{req.user_name}</p>
                                <p className="text-xs text-slate-400">{req.user_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-sm text-slate-500">{new Date(req.created_at).toLocaleDateString()}</td>
                          <td className="px-5 py-3.5"><StatusBadge status={req.status}/></td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => { setSelectedRequest(req); setActiveTab("requestDetails"); }} className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg"><Eye size={13}/></button>
                              <button onClick={() => handleApproveRequest(req.id,"approved")} disabled={req.status==="approved"} className="p-1.5 hover:bg-green-50 text-green-500 rounded-lg disabled:opacity-30"><Check size={13}/></button>
                              <button onClick={() => handleApproveRequest(req.id,"rejected")} disabled={req.status==="rejected"} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg disabled:opacity-30"><X size={13}/></button>
                              {req.status === "rejected" && (
                                <button onClick={() => handleDeleteRequest(req.id)} className="p-1.5 hover:bg-red-100 text-red-700 rounded-lg" title="Delete permanently"><Trash2 size={13}/></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              );
            })}
          </div>
        )}

        {!loading && activeTab === "requestDetails" && selectedRequest && (
          <div className="max-w-2xl space-y-4">
            <button onClick={() => setActiveTab("requests")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft size={15}/> Back</button>
            <Card>
              <CardHeader title="Request Details" action={<StatusBadge status={selectedRequest.status}/>}/>
              <div className="p-6 grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Member</p>
                  <div className="flex items-center gap-2 text-sm text-slate-700 mb-1"><User size={14} className="text-slate-400"/>{selectedRequest.user_name}</div>
                  <div className="flex items-center gap-2 text-sm text-slate-500"><FileText size={14} className="text-slate-400"/>{selectedRequest.user_email}</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Community</p>
                  <div className="flex items-center gap-2 text-sm text-slate-700 mb-1"><Layers size={14} className="text-slate-400"/>{selectedRequest.gang_name}</div>
                  <div className="flex items-center gap-2 text-sm text-slate-500"><Calendar size={14} className="text-slate-400"/>{new Date(selectedRequest.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              {selectedRequest.status === "pending" && (
                <div className="flex justify-end gap-3 px-6 pb-6 pt-2 border-t border-slate-100">
                  <Btn variant="outline" onClick={() => handleApproveRequest(selectedRequest.id,"rejected")}><X size={14}/>Reject</Btn>
                  <Btn variant="teal" onClick={() => handleApproveRequest(selectedRequest.id,"approved")}><Check size={14}/>Approve</Btn>
                </div>
              )}
              {selectedRequest.status === "rejected" && (
                <div className="flex justify-end gap-3 px-6 pb-6 pt-2 border-t border-slate-100">
                  <Btn variant="danger" onClick={() => handleDeleteRequest(selectedRequest.id)}><Trash2 size={14}/>Delete Permanently</Btn>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* ══ SPONSOR FORMS ══════════════════════════════════════════════ */}
        {!loading && activeTab === "sponsorForms" && (
          <div className="space-y-6">
            <Card>
              <CardHeader title="Upload Sponsor Form"/>
              <div className="p-6 grid grid-cols-2 gap-4">
                <Input label="Form Title *" value={newSponsorForm.title} onChange={e => setNewSponsorForm({...newSponsorForm,title:e.target.value})} placeholder="e.g., General Sponsorship Form 2025"/>
                <Input label="Version" value={newSponsorForm.version} onChange={e => setNewSponsorForm({...newSponsorForm,version:e.target.value})} placeholder="1.0"/>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                  <textarea value={newSponsorForm.description} onChange={e => setNewSponsorForm({...newSponsorForm,description:e.target.value})} rows={2}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"/>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Upload PDF *</label>
                  <input id="cheleko-sponsor-upload" type="file" accept="application/pdf"
                    onChange={e => { const f=e.target.files[0]; if(f?.type==="application/pdf") setNewSponsorForm({...newSponsorForm,form_pdf:f}); else setError("PDF only"); }}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"/>
                  {newSponsorForm.form_pdf && <p className="text-xs mt-1" style={{ color:TEAL }}>✓ {newSponsorForm.form_pdf.name}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="cheleko_active" checked={newSponsorForm.is_active} onChange={e => setNewSponsorForm({...newSponsorForm,is_active:e.target.checked})} className="w-4 h-4 rounded"/>
                  <label htmlFor="cheleko_active" className="text-xs text-slate-600">Active (available for download)</label>
                </div>
              </div>
              <div className="flex justify-end px-6 pb-6">
                <Btn onClick={handleUploadSponsorForm} disabled={loading}><Upload size={14}/> Upload Form</Btn>
              </div>
            </Card>
            <Card>
              <CardHeader title={`Available Forms (${sponsorForms.length})`}/>
              <table className="min-w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-100">{["Title","Version","Uploaded By","Downloads","Status","Actions"].map(h=><th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {sponsorForms.map(form => (
                    <tr key={form.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3.5"><p className="text-sm font-medium text-slate-800">{form.title}</p><p className="text-xs text-slate-400">{form.description}</p></td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{form.version}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{form.uploaded_by_info?.name||"N/A"}</td>
                      <td className="px-5 py-3.5 text-sm font-semibold text-slate-700">{form.download_count}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={form.is_active?"active":"inactive"}/></td>
                      <td className="px-5 py-3.5">
                        <div className="flex gap-1">
                          <button onClick={() => { ChelekoService.trackFormDownload(form.id); window.open(form.form_pdf_url,"_blank"); }} className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg"><Download size={14}/></button>
                          <button onClick={async () => { if(window.confirm("Delete this form?")) { await ChelekoService.deleteSponsorForm(form.id); fetchSponsorForms(); }}} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={14}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sponsorForms.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No sponsor forms yet</div>}
            </Card>
          </div>
        )}

        {/* ══ SPONSOR REQUESTS ═══════════════════════════════════════════ */}
        {!loading && activeTab === "sponsorRequests" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative max-w-xs">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="text" placeholder="Search requests..." value={sponsorSearchTerm} onChange={e => setSponsorSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"/>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Filter size={14} className="text-slate-400"/>
                <select value={sponsorStatusFilter} onChange={e => setSponsorStatusFilter(e.target.value)}
                  className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300">
                  <option value="all">All Status</option>
                  {["draft","submitted","approved","rejected"].map(s=><option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              </div>
            </div>
            <Card>
              <table className="min-w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-100">{["Reference","Title","Requester","Sponsors","Rate","Status",""].map(h=><th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredSponsorRequests.map(req => (
                    <React.Fragment key={req.id}>
                      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedSponsorRequest(selectedSponsorRequest===req.id?null:req.id)}>
                        <td className="px-5 py-3.5"><span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-1 rounded-lg">{req.reference_number}</span></td>
                        <td className="px-5 py-3.5 text-sm font-medium text-slate-800">{req.request_title}</td>
                        <td className="px-5 py-3.5"><p className="text-sm font-medium text-slate-800">{req.user_info?.name}</p><p className="text-xs text-slate-400">{req.user_info?.email}</p></td>
                        <td className="px-5 py-3.5">
                          <div className="flex gap-2 text-xs">
                            <span className="text-green-600 font-semibold">{req.accepted_count}✓</span>
                            <span className="text-amber-500">{req.pending_count}⏱</span>
                            <span className="text-red-500">{req.rejected_count}✗</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-100 rounded-full h-1.5 w-16">
                              <div className="h-1.5 rounded-full" style={{ width:`${req.acceptance_rate||0}%`, backgroundColor:TEAL }}/>
                            </div>
                            <span className="text-xs font-medium text-slate-600">{req.acceptance_rate?.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5"><StatusBadge status={req.status}/></td>
                        <td className="px-5 py-3.5">{selectedSponsorRequest===req.id?<ChevronUp size={15}/>:<ChevronDown size={15}/>}</td>
                      </tr>
                      {selectedSponsorRequest===req.id && (
                        <tr><td colSpan="7" className="px-5 py-5 bg-slate-50">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="bg-white rounded-xl p-4 border border-slate-100">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Description</p>
                              <p className="text-sm text-slate-600">{req.request_description||"No description"}</p>
                            </div>
                            <div className="bg-white rounded-xl p-4 border border-slate-100">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Sponsor Responses</p>
                              {req.sponsorship_responses?.map((r,i) => (
                                <div key={i} className="flex items-center justify-between py-1">
                                  <span className="text-sm text-slate-700">{r.sponsor_info?.name}</span>
                                  <StatusBadge status={r.status}/>
                                </div>
                              ))}
                              {!req.sponsorship_responses?.length && <p className="text-xs text-slate-400">No responses yet</p>}
                            </div>
                          </div>
                          {req.status==="submitted" && (
                            <div className="flex justify-end gap-3">
                              <Btn variant="outline" onClick={() => { const n=prompt("Rejection notes:"); if(n) handleAdminReview(req.id,"rejected",n); }}>
                                <XCircle size={14}/> Reject
                              </Btn>
                              <Btn variant="teal" onClick={() => { const n=prompt("Approval notes (optional):")||""; handleAdminReview(req.id,"approved",n); }}>
                                <CheckCircle size={14}/> Approve
                              </Btn>
                            </div>
                          )}
                          {req.filled_form_url && (
                            <button onClick={() => window.open(`https://api.jamboricg.com${req.filled_form_url}`,"_blank")}
                              className="mt-2 flex items-center gap-2 px-4 py-2 text-sm bg-blue-500 text-white rounded-xl">
                              <FileText size={14}/> View Filled Form
                            </button>
                          )}
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {filteredSponsorRequests.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No sponsor requests found</div>}
            </Card>
          </div>
        )}
        {/* ══ CONFIGURATION ══════════════════════════════════════════════ */}
        {activeTab === "configuration" && (
          <ChelekoConfiguration gangs={gangs} onRefresh={fetchGangs} />
        )}
      </main>

      {/* ── EDIT GANG MODAL ──────────────────────────────────────────── */}
      {editGang.id && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-800">Edit Community</h3>
              <button onClick={() => setEditGang({id:null})} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16}/></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              {[{label:"Name",key:"name"},{label:"Location",key:"location"},{label:"Contact Email",key:"contact_email",type:"email"},{label:"Contact Phone",key:"contact_phone"},{label:"Joining Fee (TZS)",key:"joining_fee"}].map(f => (
                <Input key={f.key} label={f.label} type={f.type||"text"} value={editGang[f.key]||""} onChange={e => setEditGang({...editGang,[f.key]:e.target.value})}/>
              ))}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Specification</label>
                <select value={editGang.specification||"Cheleko"} onChange={e => setEditGang({...editGang,specification:e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300">
                  <option value="All">All</option><option value="Majanga">Majanga</option><option value="Cheleko">Cheleko</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                <textarea value={editGang.description||""} onChange={e => setEditGang({...editGang,description:e.target.value})} rows={3}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"/>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="ep_c" checked={editGang.is_private||false} onChange={e => setEditGang({...editGang,is_private:e.target.checked})} className="w-4 h-4 rounded"/>
                <label htmlFor="ep_c" className="text-sm text-slate-600">Private Community</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
              <Btn variant="outline" onClick={() => setEditGang({id:null})}>Cancel</Btn>
              <Btn onClick={handleUpdateGang} disabled={loading}>Save Changes</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD CONTRIBUTION MODAL ───────────────────────────────────── */}
      {showContributionModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-800">Add Contribution</h3>
              <button onClick={() => setShowContributionModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16}/></button>
            </div>
            <div className="p-6 space-y-4">
              <Input label="Amount (TZS) *" type="number" placeholder="Enter amount" value={newContribution.amount}
                onChange={e => setNewContribution({...newContribution,amount:e.target.value})}/>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                <textarea rows={3} placeholder="Optional" value={newContribution.description}
                  onChange={e => setNewContribution({...newContribution,description:e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"/>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
              <Btn variant="outline" onClick={() => setShowContributionModal(false)}>Cancel</Btn>
              <Btn onClick={handleAddContribution} disabled={loading||!newContribution.amount}>Add Contribution</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── CONTRIBUTION HISTORY MODAL ───────────────────────────────── */}
      {showHistoryModal && contributionHistory && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <div>
                <h3 className="text-base font-bold text-slate-800">Contribution History</h3>
                <p className="text-xs text-slate-400">{contributionHistory.member_name}</p>
              </div>
              <button onClick={() => { setShowHistoryModal(false); setContributionHistory(null); }} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16}/></button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { label:"Total",    value: contributionHistory?.statistics?.total_contribution??0 },
                  { label:"Regular",  value: contributionHistory?.statistics?.regular_contributions_total??0 },
                  { label:"Average",  value: contributionHistory?.statistics?.average??0 },
                  { label:"Join Fee", value: contributionHistory?.statistics?.joining_fee_total??0 },
                ].map(s => (
                  <div key={s.label} className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
                    <p className="text-xs text-slate-500 mb-1">{s.label}</p>
                    <p className="text-sm font-bold text-slate-800">TZS {s.value.toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <table className="min-w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-100">{["Date","Amount","Type","Description","Added By"].map(h=><th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {contributionHistory.contributions?.map(c => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-500">{new Date(c.contribution_date).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm font-semibold" style={{ color:TEAL }}>TZS {parseFloat(c.amount).toLocaleString()}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.is_joining_fee?"active":"pending"}/></td>
                      <td className="px-4 py-3 text-sm text-slate-500">{c.description||"—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{c.user?.name||"System"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cheleko;